import React, { createContext, useEffect, useState, useContext } from "react";
import { APIEndpoint } from "api/index";

const ROLE_NAME_BY_LOWER = {
  admin: "Admin",
  reviewer: "Reviewer",
  "support user": "Support user",
  accountant: "Accountant",
  "data manager": "Data manager",
} as const;

const STAFF_ROLES: string[] = Object.values(ROLE_NAME_BY_LOWER);

export const normalizeRoleName = (name: string): string =>
  ROLE_NAME_BY_LOWER[name.toLowerCase() as keyof typeof ROLE_NAME_BY_LOWER] ?? name;

const withNormalizedRole = <T extends { role: { name: string } }>(userObj: T): T => ({
  ...userObj,
  role: {
    ...userObj.role,
    name: normalizeRoleName(userObj.role.name),
  },
});

//type for user Obj
export type user = {
  id: number;
  accessToken: string;
  name: string;
  email: string;
  phone: string;
  status: string;
  roleId: number;
  role: {
    id: number;
    name: string;
  };
};

//context for authentication
export const AuthContext = createContext<{
  user: user | null;
  isLoading: boolean;
  login: (userObj: user) => void;
  logout: () => void;
}>({ user: null, isLoading: false, login: () => {}, logout: () => {} });
export const useAuthContext = () => useContext(AuthContext);
const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  //current user object
  const [user, setUser] = useState<user | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(() =>
    Boolean(window.localStorage.getItem("__auth"))
  );

  //validate user's token once loaded component
  const validateToken = async () => {
    //__auth key in local storage contains email and access token for the user
    const __auth = window.localStorage.getItem("__auth");
    try {
      if (__auth) {
        const response = await fetch(`${APIEndpoint}user/me`, {
          headers: {
            Authorization: `Bearer ${__auth}`,
            "x-localization": "ar",
          },
        });
        const json = await response.json();
        if (json?.role?.name && STAFF_ROLES.includes(normalizeRoleName(json.role.name))) {
          setUser({ ...withNormalizedRole(json), accessToken: __auth });
        }
      }
    } finally {
      // Protected routes must wait for this check before redirecting to login.
      setIsLoading(false);
    }
  };

  //method for user login
  const login = (userObj: user) => {
    //set the token and the email in the local storage for later verifications
    const __auth = userObj.accessToken;
    window.localStorage.setItem("__auth", __auth);
    //login the user
    setUser(withNormalizedRole(userObj));
    setIsLoading(false);
  };
  const logout = () => {
    setUser(null);
    setIsLoading(false);
    window.localStorage.setItem("__auth", "");
  };
  useEffect(() => {
    validateToken();
    //event listener to set the refresh flag
    window.onbeforeunload = () => {
      window.localStorage.setItem("rflag", new Date().getTime().toString());
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
